import { onIdTokenChanged, type User } from 'firebase/auth';
import { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef, type ReactNode } from 'react';
import { ApiClientError, api } from '@/services/api';
import { signIn, signOut } from '@/services/authService';
import { getFirebase } from '@/services/firebase';
import { DEMO_ME } from '@/demo/demoStore';
import { DEMO_CREDENTIALS, IS_DEMO } from '@/demo/demoMode';
import { setErrorReporterUser } from '@/lib/errorReporter';
import { authReducer, initialAuthStatus, type AuthStatus } from './authState';

interface AuthContextValue {
  status: AuthStatus;
  /** משתמש Firebase הנוכחי — לשליחת טוקן בקריאות API. */
  user: User | null;
  signIn: (email: string, password: string, remember?: boolean) => Promise<void>;
  signOut: () => Promise<void>;
  retry: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);
const DEMO_REMEMBER_KEY = 'sa-demo-signed-in';

export function AuthProvider({ children }: { children: ReactNode }) {
  return IS_DEMO ? <DemoAuthProvider>{children}</DemoAuthProvider> : <FirebaseAuthProvider>{children}</FirebaseAuthProvider>;
}

/**
 * מצב תצוגה בלבד: התחברות מקומית עם פרטי הדוגמה, בלי Firebase ובלי שרת.
 * לא קיים בבנייה לפרודקשן (IS_DEMO=false והענף נמחק).
 */
function DemoAuthProvider({ children }: { children: ReactNode }) {
  // במצב תצוגה "זכור אותי" נשמר בדפדפן, כדי שרענון לא ינתק
  const [status, dispatch] = useReducer(authReducer, { kind: 'signed_out', notice: null } as AuthStatus, (s) => {
    try {
      const stored = localStorage.getItem(DEMO_REMEMBER_KEY) ?? sessionStorage.getItem(DEMO_REMEMBER_KEY);
      if (stored === '1') return { kind: 'authorized', me: DEMO_ME } as AuthStatus;
    } catch {
      /* אחסון חסום */
    }
    return s;
  });
  const demoUser = useMemo(() => ({ uid: DEMO_ME.uid, getIdToken: async () => 'demo' }) as unknown as User, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user: status.kind === 'authorized' ? demoUser : null,
      signIn: async (email: string, password: string, remember = true) => {
        await new Promise((r) => setTimeout(r, 400));
        if (email.trim().toLowerCase() !== DEMO_CREDENTIALS.email || password !== DEMO_CREDENTIALS.password) {
          throw new Error('במצב תצוגה יש להתחבר עם פרטי הדוגמה שמופיעים מעל הטופס');
        }
        try {
          (remember ? localStorage : sessionStorage).setItem(DEMO_REMEMBER_KEY, '1');
        } catch {
          /* אחסון חסום */
        }
        dispatch({ type: 'firebase_user', uid: DEMO_ME.uid });
        dispatch({ type: 'me_ok', me: DEMO_ME });
      },
      signOut: async () => {
        try {
          localStorage.removeItem(DEMO_REMEMBER_KEY);
          sessionStorage.removeItem(DEMO_REMEMBER_KEY);
        } catch {
          /* אחסון חסום */
        }
        dispatch({ type: 'firebase_user', uid: null });
      },
      retry: () => undefined,
    }),
    [status, demoUser],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function FirebaseAuthProvider({ children }: { children: ReactNode }) {
  const [status, dispatch] = useReducer(authReducer, initialAuthStatus);
  const userRef = useRef<User | null>(null);
  const verifyingFor = useRef<string | null>(null);

  const verify = useCallback(async (user: User) => {
    if (verifyingFor.current === user.uid) return;
    verifyingFor.current = user.uid;
    try {
      const me = await api.me(user);
      dispatch({ type: 'me_ok', me });
    } catch (err) {
      if (err instanceof ApiClientError && err.code === 'forbidden') {
        dispatch({ type: 'me_denied' });
        await signOut();
      } else {
        dispatch({
          type: 'me_failed',
          message: err instanceof ApiClientError ? err.message : 'לא הצלחנו לאמת את ההרשאה מול השרת',
        });
      }
    } finally {
      verifyingFor.current = null;
    }
  }, []);

  useEffect(() => {
    const { auth } = getFirebase();
    // onIdTokenChanged מכסה גם התחברות/יציאה וגם רענון טוקן.
    return onIdTokenChanged(auth, (user) => {
      const previous = userRef.current;
      userRef.current = user;
      setErrorReporterUser(user);
      dispatch({ type: 'firebase_user', uid: user?.uid ?? null });
      if (user && previous?.uid !== user.uid) void verify(user);
    });
  }, [verify]);

  const retry = useCallback(() => {
    const user = userRef.current;
    if (!user) return;
    dispatch({ type: 'retry' });
    void verify(user);
  }, [verify]);

  const value = useMemo<AuthContextValue>(
    () => ({ status, user: userRef.current, signIn, signOut, retry }),
    [status, retry],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

/** לשימוש בתוך מסכי ניהול בלבד — שם המשתמש כבר מאושר. */
export function useAdmin() {
  const { status, user } = useAuth();
  if (status.kind !== 'authorized' || !user) throw new Error('useAdmin used outside an authorized area');
  return { me: status.me, user };
}
